//@flow
import * as Immutable from 'immutable';

import { Shift } from 'common/models/flow/Shift';
import type { ShiftModelType } from 'common/models/flow/Shift';
import { TeamId } from './TeamId';
import { AuditMetadata } from './AuditMetadata';

import { DayOfWeekType } from 'common/enums/DateTimeEnums';

//Validation stuff
import {
    validate,
    validatable,
    validateAll,
    softValidateAll,
    validateId
} from 'common/utils/ValidationUtil';
import RegExUtil from 'common/utils/RegExUtil';
import StringUtils from 'common/utils/StringUtils';
import DataUtils from 'common/utils/DataUtils';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import I18nHelper from 'common/helpers/I18nHelper';

import { DayShift } from './DayShift';
import { ENTITY_NAME_ID } from 'common/constants/entity/shiftMaster';

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

@validatable
class InternalShift extends Shift {
    static fromJS(json: ShiftModelType): InternalShift {
        const state: Object = Object.assign({}, json);
        state.teamId = state.teamId
            ? TeamId.fromJS(state.teamId)
            : state.teamId;
        state.workingDays = Immutable.List(state.workingDays).map(item =>
            DayShift.fromJS(item)
        );
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    initializeWeekDays() {
        const days = Object.values(DayOfWeekType);
        const sortedWorkingDays = [];
        days.forEach(day => {
            const slot = this.workingDays.find(dayShift => {
                return dayShift.dayOfWeek === day;
            });
            if (slot) {
                sortedWorkingDays.push(slot);
            } else {
                const shift = DayShift.fromJS({
                    dayOfWeek: day,
                    slot: { start: '', end: '' },
                    breaks: [],
                    startLocation: { latLng: {}, address: '' },
                    endLocation: { latLng: {}, address: '' }
                });
                sortedWorkingDays.push(shift);
            }
        });

        return Immutable.List(sortedWorkingDays);
    }

    getDefaultSlots(): Immutable.List<Object> {
        const days = Object.values(DayOfWeekType);
        const sortedWorkingDays = [];
        days.forEach(day => {
            const dayShift = DayShift.fromJS({
                dayOfWeek: day,
                slot: { start: '', end: '' },
                breaks: []
            });
            sortedWorkingDays.push(dayShift);
        });
        return Immutable.List(sortedWorkingDays);
    }

    hasLocation = () => {
        const { workingDays } = this;
        return workingDays.some(day => day?.startLocation && day?.endLocation);
    };

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            StringUtils.isEmpty(this.name) ||
            StringUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    setTeamId(teamId: ?TeamId): this {
        return super.setTeamId(teamId);
    }

    @validate
    setWorkingDays(workingDays: Immutable.List<DayShift>): this {
        return super.setWorkingDays(workingDays);
    }

    validateWorkingDays() {
        if (
            DataUtils.isEmpty(this.workingDays) ||
            this.workingDays.size <= 0 ||
            this.workingDays.every(
                dayShift =>
                    !dayShift.slot ||
                    dayShift.slot?.isEmpty() ||
                    !dayShift.slot.start
            )
        ) {
            return nsTranslate('shiftValidationErrors.workingDaysEmpty');
        }
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { workingDays } = this;
        if (workingDays) {
            return workingDays.some(dayShift => dayShift.hasValidationErrors());
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { workingDays } = this;
        if (workingDays) {
            workingDays = workingDays.map(dayShift =>
                dayShift.setAllValidationErrors()
            );
        }

        return this.setWorkingDays(workingDays);
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    getDayShiftForDay = (day: string) => {
        const _day = day.toUpperCase();
        const dayShift = this.workingDays.find(
            workingDay => workingDay.dayOfWeek === _day
        );

        return dayShift?.slot?.start && dayShift?.slot?.end
            ? dayShift
            : DayShift.fromJS({
                  dayOfWeek: _day,
                  slot: { start: '8:00', end: '18:00' },
                  breaks: [],
                  startLocation: { latLng: {}, address: '' },
                  endLocation: { latLng: {}, address: '' }
              });
    };

    get teamId(): ?TeamId {
        return this._state.get('teamId');
    }

    get workingDays(): Immutable.List<DayShift> {
        return this._state.get('workingDays');
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedShift = super.defaultSanitize();
        if (sanitizedShift) {
            const sanitizedWorkingdays = sanitizedShift.workingDays
                .map(workingDay => workingDay.defaultSanitize())
                .filter(workingDay => workingDay.slot);
            return sanitizedShift.setWorkingDays(sanitizedWorkingdays);
        }
        return sanitizedShift;
    }

    static defaultInit(): InternalShift {
        return InternalShift.fromJS({});
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }
}

export { InternalShift as Shift };
